import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Clock, FileText, Briefcase, TrendingUp, Users, Activity, Calendar, AlertTriangle, CheckCircle, MessagesSquare, BellRing } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { pushSupported, permissionState } from '../lib/push';
import MySheetSchedule from './MySheetSchedule';
import FleetAlertsCard from './FleetAlertsCard';
import PaperworkCard from './PaperworkCard';
import ClockInAckCard from './ClockInAckCard';
import ToolCribTiles, { isCribUser } from './ToolCribTiles';
import DailyBriefCard from './DailyBriefCard';
import OpsCells from './OpsCells';
import MailWidget from './mail/MailWidget';
import CalendarStrip from './mail/CalendarStrip';
import TimeOffCard from './TimeOffCard';
import TrainingScheduledCard from './TrainingScheduledCard';
import { UnpackedBoxesBanner } from './UnpackedBoxesBanner';
import { FiscalSwitchBanner } from './FiscalSwitchBanner';
import { SalesLeaderboard } from './SalesLeaderboard';
import BoardsPage from './boards/BoardsPage';
import { FY } from '../lib/fiscal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ── Role classification helpers ──
const FINANCIAL_ROLES = ['super_admin', 'office_admin', 'project_manager_estimator'];
const HR_ROLES = ['office_admin'];
const FOREMAN_ROLES = ['shop_foreman', 'fabrication_manager', 'millwright_foreman'];
const WORKER_ROLES = ['millwright', 'welder', 'labourer', 'tool_crib', 'dispatch'];
const MAIL_ROLES = ['super_admin', 'admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'dispatch', 'fabrication_manager'];

function getRoleGroup(role) {
  if (FINANCIAL_ROLES.includes(role)) return 'financial';
  if (HR_ROLES.includes(role)) return 'hr';
  if (FOREMAN_ROLES.includes(role)) return 'foreman';
  return 'worker';
}

// ══════════════════════════════════════════════════════════════
// MY DISPATCHES — in-app dispatch notices + Site Info folder viewer
// ══════════════════════════════════════════════════════════════
function SiteInfoModal({ wo, onClose }) {
  const [files, setFiles] = useState(null);
  const [error, setError] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/dispatch/site-info/${encodeURIComponent(wo)}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setFiles(r.data.files || []))
      .catch(e => setError(e.response?.data?.detail || 'Could not load the Site info folder'));
  }, [wo]);

  const openFile = async (f) => {
    setOpeningId(f.id);
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/dispatch/site-info/${encodeURIComponent(wo)}/file/${f.id}`, {
        headers: { Authorization: `Bearer ${token}` }, responseType: 'blob'
      });
      const url = URL.createObjectURL(r.data);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error('Could not open the file');
    } finally {
      setOpeningId(null);
    }
  };

  const icon = (mt) => (mt || '').startsWith('image/') ? '🖼️' : (mt || '').includes('pdf') ? '📄' : '📎';

  return createPortal(
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col" onClick={e => e.stopPropagation()} data-testid="site-info-modal">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
          <div>
            <h3 className="text-slate-900 font-bold">Site Info — {wo}</h3>
            <p className="text-xs text-slate-500">Drawings & pictures from the project folder</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 text-xl leading-none" data-testid="site-info-close">✕</button>
        </div>
        <div className="p-4 overflow-y-auto">
          {error && <p className="text-sm text-red-600" data-testid="site-info-error">{error}</p>}
          {!error && files === null && (
            <div className="flex justify-center py-6"><div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-400"></div></div>
          )}
          {!error && files !== null && files.length === 0 && (
            <p className="text-sm text-slate-500 text-center py-6" data-testid="site-info-empty">
              Nothing in the Site info folder yet — the office can drop drawings or pictures in there anytime.
            </p>
          )}
          {!error && files && files.length > 0 && (
            <div className="space-y-2">
              {files.map(f => (
                <button key={f.id} onClick={() => openFile(f)} disabled={openingId === f.id}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg bg-slate-50 hover:bg-slate-100 border border-slate-200 text-left disabled:opacity-60"
                  data-testid={`site-info-file-${f.id}`}>
                  <span className="text-lg">{icon(f.mimeType)}</span>
                  <span className="flex-1 min-w-0">
                    <span className="block text-sm text-slate-900 font-medium truncate">{f.name}</span>
                    <span className="block text-[11px] text-slate-500">{openingId === f.id ? 'Opening…' : 'Tap to view'}</span>
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PushNudge() {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => sessionStorage.getItem('push_nudge_hidden') === '1');
  const off = !pushSupported() || permissionState() !== 'granted' || !localStorage.getItem('push_subscribed');
  if (hidden || !off) return null;
  return (
    <div className="flex items-center gap-3 bg-white border border-amber-300 border-l-4 border-l-amber-500 rounded-xl px-4 py-3 shadow-sm" data-testid="dashboard-push-nudge">
      <BellRing className="w-5 h-5 text-amber-600 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-900 font-bold">Dispatches now come through 365Ops — turn on notifications</p>
        <p className="text-xs text-slate-600">One tap so you never miss a job. Works on iPhone (from the Home Screen app) and Android.</p>
      </div>
      <button onClick={() => navigate('/notifications')} className="flex-shrink-0 bg-amber-500 hover:bg-amber-400 text-slate-950 text-xs font-bold px-3 py-1.5 rounded-lg shadow-sm transition-colors" data-testid="dashboard-push-nudge-btn">Turn on</button>
      <button onClick={() => { sessionStorage.setItem('push_nudge_hidden', '1'); setHidden(true); }} className="text-slate-400 hover:text-slate-700 text-sm p-1" title="Later" data-testid="dashboard-push-nudge-dismiss">✕</button>
    </div>
  );
}

export function MyDispatches() {
  const [dispatches, setDispatches] = useState([]);
  const [siteInfoWO, setSiteInfoWO] = useState(null);
  const [replying, setReplying] = useState(null);
  const navigate = useNavigate();

  const load = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/dispatch/my-dispatches`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setDispatches(r.data.dispatches || []))
      .catch(() => {});
  };
  useEffect(load, []);

  const quickReply = async (d, value) => {
    setReplying(d.id);
    try {
      await axios.post(`${API}/dispatch-chat/threads/${d.thread_id}/reply`, { value }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      toast.success(value === 'Y' ? "You're confirmed ✓" : 'Marked as unavailable — dispatch has been notified');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not send your reply'); }
    setReplying(null);
  };

  if (dispatches.length === 0) return <MyCrewClockOut />;

  const fmtD = (d) => d ? new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '';
  const fmtT = (t) => { try { return new Date(`2000-01-01T${t}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }); } catch { return t; } };

  return (
    <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm border-l-4 border-l-blue-600 p-5 mb-6" data-testid="my-dispatches-card">
      <MyCrewClockOut />
      <h3 className="text-xs font-bold text-blue-900 uppercase tracking-wider mb-3 flex items-center gap-2">
        <Briefcase className="w-4 h-4" /> My Dispatches
      </h3>
      <div className="space-y-3">
        {dispatches.map(d => (
          <div key={d.id} className="bg-slate-50 rounded-lg border border-slate-200 p-4" data-testid={`my-dispatch-${d.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-slate-900 font-bold">
                  <span className="font-mono text-sky-800">{d.work_order_number}</span>
                  {d.customer_name ? ` — ${d.customer_name}` : ''}
                  {d.my_status === 'confirmed' && <span className="ml-2 text-xs text-emerald-700 font-semibold" data-testid={`my-dispatch-status-${d.id}`}>✓ Confirmed</span>}
                  {d.my_status === 'declined' && <span className="ml-2 text-xs text-red-700 font-semibold" data-testid={`my-dispatch-status-${d.id}`}>✗ Declined</span>}
                </p>
                <p className="text-sm text-slate-700 mt-0.5">
                  {fmtD(d.start_date)}{d.end_date && d.end_date !== d.start_date ? ` → ${fmtD(d.end_date)}` : ''} · {fmtT(d.start_time)}–{fmtT(d.end_time)}
                </p>
                {d.job_site && <p className="text-xs text-slate-500 mt-0.5">{d.job_site}</p>}
                {d.site_contact && (
                  <p className="text-xs text-slate-500 mt-0.5">
                    Contact: {d.site_contact}{d.site_contact_phone ? ` · ${d.site_contact_phone}` : ''}
                  </p>
                )}
              </div>
              <div className="flex flex-col gap-1.5 flex-shrink-0">
                {d.thread_id && (
                  <button onClick={() => navigate(`/dispatch-chat?thread=${d.thread_id}`)}
                    className="px-3 py-2 bg-white hover:bg-slate-50 text-slate-800 border border-slate-300 rounded-lg text-xs font-semibold flex items-center gap-1.5 shadow-sm"
                    data-testid={`my-dispatch-chat-btn-${d.id}`}>
                    <MessagesSquare className="w-3.5 h-3.5" /> Job Chat
                  </button>
                )}
                <button
                  onClick={() => setSiteInfoWO(d.work_order_number)}
                  className="px-3 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold flex items-center gap-1.5"
                  data-testid={`site-info-btn-${d.id}`}
                >
                  <FileText className="w-3.5 h-3.5" /> Site Info
                </button>
              </div>
            </div>
            {d.thread_id && d.my_status === 'sent' && (
              <div className="mt-3 pt-3 border-t border-slate-200 grid grid-cols-2 gap-2" data-testid={`my-dispatch-reply-${d.id}`}>
                <button onClick={() => quickReply(d, 'Y')} disabled={replying === d.id} className="bg-emerald-600 hover:bg-emerald-500 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg text-sm" data-testid={`my-dispatch-yes-${d.id}`}>✓ Confirm — I'm in</button>
                <button onClick={() => quickReply(d, 'N')} disabled={replying === d.id} className="bg-red-600 hover:bg-red-500 disabled:opacity-60 text-white font-bold py-2.5 rounded-lg text-sm" data-testid={`my-dispatch-no-${d.id}`}>✗ Can't make it</button>
              </div>
            )}
          </div>
        ))}
      </div>
      {siteInfoWO && <SiteInfoModal wo={siteInfoWO} onClose={() => setSiteInfoWO(null)} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// WORKER DASHBOARD — Hours focused, no dollar amounts
// ══════════════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════════════
// MY FIELD WEEK — the employee's own field hours by work order (Sun–Sat)
// ══════════════════════════════════════════════════════════════
export function MyFieldWeek() {
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState(null);
  const [hasAny, setHasAny] = useState(null); // hide the card for shop-only guys with no field time

  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/field/my-week`, { headers: { Authorization: `Bearer ${token}` }, params: { week_offset: offset } })
      .then(r => {
        setData(r.data);
        if (offset === 0 && hasAny === null) {
          if (r.data.total_hours > 0 || r.data.open_event) setHasAny(true);
          else axios.get(`${API}/field/my-week`, { headers: { Authorization: `Bearer ${token}` }, params: { week_offset: 1 } })
            .then(r2 => setHasAny(r2.data.total_hours > 0)).catch(() => setHasAny(false));
        }
      })
      .catch(() => { if (hasAny === null) setHasAny(false); });
  }, [offset]);

  if (!hasAny || !data) return null;

  const dayLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' }).charAt(0);
  const fmtRange = `${new Date(`${data.week_start}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} – ${new Date(`${data.week_end}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  const fmtH = (h) => (Math.round(h * 100) / 100).toString().replace(/\.0+$/, '');

  return (
    <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-5 mb-6" data-testid="my-field-week-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1.5"><Briefcase className="w-3.5 h-3.5" /> My Field Hours by Work Order</p>
          <p className="text-[11px] text-slate-500 mt-0.5">{offset === 0 ? 'This week' : offset === 1 ? 'Last week' : `${offset} weeks ago`} · {fmtRange}</p>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setOffset(o => o + 1)} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs" data-testid="my-field-week-prev" title="Previous week">‹</button>
          <button onClick={() => setOffset(o => Math.max(0, o - 1))} disabled={offset === 0} className="p-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 disabled:opacity-30 text-slate-700 text-xs" data-testid="my-field-week-next" title="Next week">›</button>
        </div>
      </div>

      {data.open_event && (
        <p className="text-xs text-emerald-700 mb-3" data-testid="my-field-week-open">● Clocked in now on <span className="font-mono">{data.open_event.work_order_number}</span> — today's hours show after you clock out.</p>
      )}

      {data.work_orders.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="my-field-week-empty">No field hours this week.</p>
      ) : (
        <div className="space-y-2">
          {data.work_orders.map(wo => (
            <div key={wo.work_order_number} className="bg-slate-50 border border-slate-200 rounded-lg px-3 py-2.5" data-testid={`my-field-week-wo-${wo.work_order_number}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-slate-900 font-semibold text-sm truncate"><span className="font-mono text-sky-800">{wo.work_order_number}</span>{wo.customer ? <span className="text-slate-600 font-normal"> — {wo.customer}</span> : null}</p>
                  {wo.pending_hours > 0 && wo.approved_hours > 0 && (
                    <p className="text-[10px] text-slate-500">{fmtH(wo.approved_hours)}h approved · {fmtH(wo.pending_hours)}h pending</p>
                  )}
                  {wo.pending_hours > 0 && wo.approved_hours === 0 && <p className="text-[10px] text-amber-700">pending PM approval</p>}
                  {wo.pending_hours === 0 && wo.approved_hours > 0 && <p className="text-[10px] text-emerald-700">approved</p>}
                </div>
                <p className="text-xl font-bold text-slate-900 flex-shrink-0" data-testid={`my-field-week-hours-${wo.work_order_number}`}>{fmtH(wo.hours)}<span className="text-xs text-slate-500 font-normal">h</span></p>
              </div>
              <div className="grid grid-cols-7 gap-1 mt-2">
                {data.dates.map(d => {
                  const h = wo.days[d] || 0;
                  const isToday = d === new Date().toLocaleDateString('en-CA', { timeZone: 'America/Toronto' });
                  return (
                    <div key={d} className={`rounded text-center py-1 ${h > 0 ? 'bg-blue-100 text-blue-900' : 'bg-slate-100 text-slate-400'} ${isToday ? 'ring-1 ring-blue-500' : ''}`} title={d}>
                      <p className="text-[9px] uppercase leading-none">{dayLabel(d)}</p>
                      <p className="text-[11px] font-semibold leading-tight mt-0.5">{h > 0 ? fmtH(h) : '·'}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          <div className="flex items-center justify-between pt-1 text-sm">
            <span className="text-slate-600">Week total{data.pending_hours > 0 ? <span className="text-[11px] text-amber-700 ml-2">{fmtH(data.pending_hours)}h awaiting approval</span> : null}</span>
            <span className="text-slate-900 font-bold" data-testid="my-field-week-total">{fmtH(data.total_hours)}h</span>
          </div>
        </div>
      )}
    </div>
  );
}

export function WorkerDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/shop/dashboard-stats`, config).catch(() => ({ data: {} })),
      axios.get(`${API}/shop/clock-status`, config).catch(() => ({ data: {} })),
    ]).then(([statsR, clockR]) => {
      setStats({ ...statsR.data, clock: clockR.data });
      setLoading(false);
    });
  }, []);

  if (loading) return <DashboardSkeleton name={user?.full_name} />;

  const clockedIn = stats?.clock?.clocked_in && !stats?.clock?.clocked_out;

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Hey {user?.full_name?.split(' ')[0]}</h1>
        <p className="text-slate-600 text-sm mt-1">Here's your week at a glance</p>
      </div>

      {/* Clock Status Banner */}
      <div className={`rounded-xl p-5 mb-6 border bg-white/95 shadow-sm border-l-4 ${clockedIn ? 'border-emerald-200 border-l-emerald-600' : 'border-slate-200/90 border-l-slate-400'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-3 h-3 rounded-full ${clockedIn ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
            <div>
              <p className="text-slate-900 font-semibold">{clockedIn ? 'Clocked In' : 'Not Clocked In'}</p>
              <p className="text-xs text-slate-500">{clockedIn ? 'Have a great shift!' : 'Head to Shop Tracker to clock in'}</p>
            </div>
          </div>
          {clockedIn && stats?.clock?.event && (
            <p className="text-sm text-emerald-700 font-mono">
              Since {new Date(stats.clock.event.clock_in_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'America/Toronto' })}
            </p>
          )}
        </div>
      </div>

      {/* Crew this person clocked in on their PSI (anyone can run the PSI) */}
      <MyCrewClockOut />

      {/* My Dispatches */}
      <MyDispatches />

      {/* My field hours by WO (only shows for people with field time) */}
      <MyFieldWeek />

      {/* Hours Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">This Week</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.my_week_regular || 0}h</p>
          {(stats?.my_week_overtime || 0) > 0 && (
            <p className="text-sm text-amber-700 mt-1">+ {stats.my_week_overtime}h OT</p>
          )}
        </div>
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-5">
          <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold">Last Week</p>
          <p className="text-3xl font-bold text-slate-900 mt-2">{stats?.last_week_regular || 0}h</p>
          {(stats?.last_week_overtime || 0) > 0 && (
            <p className="text-sm text-amber-700 mt-1">+ {stats.last_week_overtime}h OT</p>
          )}
        </div>
      </div>

      {/* Attendance */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-amber-700">{stats?.ytd_late || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Late (YTD)</p>
        </div>
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-red-700">{stats?.ytd_absent || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Absent (YTD)</p>
        </div>
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-4 text-center">
          <p className="text-2xl font-bold text-blue-700">{stats?.ytd_sick || 0}</p>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Sick (YTD)</p>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MY PSI CREW — whoever filled in the PSI is the foreman for that crew: clock them out from here
// ══════════════════════════════════════════════════════════════
export function MyCrewClockOut() {
  const [crew, setCrew] = useState(null);
  const [busy, setBusy] = useState(null);
  const config = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const load = () => axios.get(`${API}/shop/my-crew`, config()).then(r => setCrew(r.data.crew || [])).catch(() => setCrew([]));
  useEffect(() => { load(); }, []);
  if (!crew || crew.length === 0) return null;
  const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Toronto' }) : '';
  const wos = [...new Set(crew.map(c => c.work_order_number).filter(Boolean))];
  const out = async (c) => {
    if (!window.confirm(`Clock out ${c.employee_name}?`)) return;
    setBusy(c.id);
    try {
      await axios.post(`${API}/shop/clock-out-crew`, c.is_contractor ? { contractor_ids: [c.id] } : { employee_ids: [c.employee_id] }, config());
      toast.success(`${c.employee_name} clocked out`); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not clock out'); }
    setBusy(null);
  };
  const outAll = async () => {
    if (!window.confirm(`Clock out all ${crew.length} crew you clocked in on your PSI?`)) return;
    setBusy('all');
    try {
      const r = await axios.post(`${API}/shop/clock-out-crew`, { mine: true }, config());
      toast.success(`Clocked out ${r.data.clocked_out || 0} crew`); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not clock out'); }
    setBusy(null);
  };
  return (
    <div className="bg-white/95 rounded-xl border border-emerald-200 shadow-sm border-l-4 border-l-emerald-600 p-4 mb-6" data-testid="my-crew-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-slate-900 font-semibold flex items-center gap-2"><Users className="w-4 h-4 text-emerald-600" /> Your crew on site ({crew.length})</p>
          <p className="text-xs text-slate-500">You clocked them in on your PSI{wos.length ? ` · WO ${wos.join(', ')}` : ''} — clock them out when the day's done.</p>
        </div>
        <button onClick={outAll} disabled={busy === 'all'} className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white" data-testid="my-crew-clock-out-all">
          <Clock className="w-3.5 h-3.5" /> {busy === 'all' ? 'Clocking out…' : `Clock Out Crew (${crew.length})`}
        </button>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {crew.map(c => (
          <span key={c.id} className="inline-flex items-center gap-2 text-xs bg-emerald-50 border border-emerald-200 rounded-full pl-3 pr-1 py-1 text-slate-800" data-testid={`my-crew-${c.employee_id || c.id}`}>
            {c.employee_name}{c.is_contractor && <span className="text-slate-500">(contractor)</span>} <span className="text-slate-500">since {fmt(c.clock_in_time)}</span>
            <button onClick={() => out(c)} disabled={busy === c.id} className="text-[11px] font-bold text-red-700 bg-white border border-red-200 rounded-full px-2 py-0.5 hover:bg-red-50 disabled:opacity-50" data-testid={`my-crew-out-${c.employee_id || c.id}`}>out</button>
          </span>
        ))}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// FOREMAN DASHBOARD — Crew + hours, no dollar amounts
// ══════════════════════════════════════════════════════════════
export function ForemanDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [crewStatus, setCrewStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clockingOut, setClockingOut] = useState(false);

  const fetchCrew = () => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    return Promise.all([
      axios.get(`${API}/shop/dashboard-stats`, config).catch(() => ({ data: {} })),
      axios.get(`${API}/shop/job/crew-status?include_field=true`, config).catch(() => ({ data: {} })),
    ]).then(([statsR, crewR]) => {
      setStats(statsR.data);
      setCrewStatus(crewR.data);
      setLoading(false);
    });
  };

  useEffect(() => { fetchCrew(); }, []);

  const crew = crewStatus?.crew || [];
  const onSite = crew.filter(c => !c.clock_out_time).length;
  const clockedOut = crew.filter(c => c.clock_out_time).length;
  const pending = (stats?.pending_time_entries || 0) + (stats?.pending_consumable_entries || 0);

  // Crew this foreman clocked in (via their PSI) who are still on site
  const myOpenCrew = crew.filter(c => !c.clock_out_time && c.clocked_in_by && c.clocked_in_by === user?.full_name);

  const clockOutMyCrew = async () => {
    if (myOpenCrew.length === 0) return;
    if (!window.confirm(`Clock out ${myOpenCrew.length} crew member(s) you clocked in on your PSI?`)) return;
    setClockingOut(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const r = await axios.post(`${API}/shop/clock-out-crew`, { mine: true }, config);
      toast.success(`Clocked out ${r.data.clocked_out || 0} crew member(s)`);
      await fetchCrew();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to clock out crew');
    }
    setClockingOut(false);
  };

  if (loading) return <DashboardSkeleton name={user?.full_name} />;

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Foreman Dashboard</h1>
        <p className="text-slate-600 text-sm mt-1">Crew status & approvals</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="On Site" value={onSite} color="emerald" />
        <StatCard label="Clocked Out" value={clockedOut} color="slate" />
        <StatCard label="Pending Approvals" value={pending} color="amber" />
        <StatCard label="Total Crew" value={crew.length} color="blue" />
      </div>

      {/* My Dispatches */}
      <MyDispatches />
      <MyFieldWeek />

      {/* Quick crew list */}
      <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/70 flex items-center justify-between gap-3">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
            <Users className="w-4 h-4 text-blue-600" /> Today's Crew
          </h3>
          {myOpenCrew.length > 0 && (
            <button
              onClick={clockOutMyCrew}
              disabled={clockingOut}
              data-testid="foreman-clock-out-crew-btn"
              className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white transition-colors"
            >
              <Clock className="w-3.5 h-3.5" />
              {clockingOut ? 'Clocking out…' : `Clock Out Crew (${myOpenCrew.length})`}
            </button>
          )}
        </div>
        {crew.length === 0 ? (
          <p className="px-5 py-6 text-slate-500 text-center text-sm">No one clocked in today</p>
        ) : (
          <div className="divide-y divide-slate-100">
            {crew.slice(0, 12).map(c => (
              <div key={c.employee_id} className="px-5 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${c.clock_out_time ? 'bg-slate-400' : 'bg-emerald-500'}`} />
                  <span className="text-sm text-slate-900">{c.employee_name}</span>
                  {c.is_late && <span className="text-[10px] text-red-800 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded">{c.late_minutes}m late</span>}
                </div>
                <span className="text-sm text-slate-600 font-mono">{c.total_hours_today?.toFixed(1)}h</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// FINANCIAL DASHBOARD — Full data (PM, Estimator, Admin)
// ══════════════════════════════════════════════════════════════
export function FinancialDashboard({ user }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [recentQuotes, setRecentQuotes] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    const fetchData = async () => {
      try {
        const [statsR, quotesR, projectsR] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, config),
          axios.get(`${API}/quotes`, config).catch(() => ({ data: [] })),
          axios.get(`${API}/projects`, config).catch(() => ({ data: [] })),
        ]);
        setStats(statsR.data);

        const fn = (user?.full_name || '').split(' ')[0]?.toLowerCase();
        const ln = (user?.full_name || '').split(' ')[1] || '';
        const initials = ln ? `${fn.charAt(0)}${ln.charAt(0)}`.toUpperCase() : '';

        const myQuotes = quotesR.data
          .filter(q => {
            const est = (q.estimator || '').toLowerCase();
            const pm = (q.project_manager || '').toLowerCase();
            return est.includes(initials.toLowerCase()) || est.includes(fn) || pm.includes(fn) || q.created_by === user?.id;
          })
          .sort((a, b) => (b.quote_number || '').localeCompare(a.quote_number || '', undefined, { numeric: true }))
          .slice(0, 5);
        setRecentQuotes(myQuotes);

        const myProjects = projectsR.data
          .filter(p => (p.assigned_manager || '').toLowerCase().includes(fn))
          .sort((a, b) => (b.project_number || '').localeCompare(a.project_number || '', undefined, { numeric: true }));
        setRecentProjects(myProjects);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    fetchData();
  }, [user]);

  const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

  if (loading) return <DashboardSkeleton name={user?.full_name} />;

  const activeProjects = recentProjects
    .filter(p => p.status === 'in_progress' || p.status === 'active')
    .sort((a, b) => (b.project_number || '').localeCompare(a.project_number || '', undefined, { numeric: true }));

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Dashboard</h1>
        <p className="text-slate-600 text-sm mt-1">Welcome back, {user?.full_name}</p>
      </div>

      {stats.is_pm && stats.my_revenue > 0 && (
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-xl p-6 mb-6 shadow-sm">
          <p className="text-emerald-100 text-xs font-semibold uppercase tracking-wider">My Revenue</p>
          <p className="text-4xl font-black text-white mt-1">{fmt(stats.my_revenue)}</p>
          <p className="text-emerald-200 text-sm mt-1">{stats.total_projects} Projects / {stats.active_projects} Active</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <StatCard label={stats.is_pm ? 'My Quotes' : `${FY.long} Quotes`} value={stats.total_quotes} color="orange" />
        <StatCard label="Active Projects" value={stats.active_projects} color="blue" />
        <StatCard label="Safety Tickets" value={stats.total_safety_tickets} color="green" />
        <StatCard label="Expiring Soon" value={stats.expiring_soon} color="amber" />
      </div>

      {/* Active Projects */}
      {activeProjects.length > 0 && (
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm mb-6 overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/70">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
              <Activity className="w-4 h-4 text-emerald-600" /> Active Projects
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {activeProjects.slice(0, 8).map(p => (
              <div key={p.id} className="px-5 py-2.5 flex items-center justify-between cursor-pointer hover:bg-blue-50/50 transition-colors group"
                onClick={() => navigate(`/projects/${p.project_number}`)}>
                <div>
                  <span className="font-mono text-sky-800 font-bold text-sm group-hover:text-sky-950">{p.project_number}</span>
                  <span className="text-slate-800 ml-2 text-sm">{p.client_name}</span>
                </div>
                <span className={((p.total_invoiced || p.budget || p.estimated_budget) > 0) ? 'text-emerald-700 font-bold tabular-nums' : 'text-slate-400'} data-testid={`active-project-value-${p.project_number}`}>
                  {(p.total_invoiced || p.budget || p.estimated_budget) > 0 ? fmt(p.total_invoiced || p.budget || p.estimated_budget) : '—'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent Quotes */}
      {recentQuotes.length > 0 && (
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50/70">
            <h3 className="text-xs font-bold uppercase tracking-wider text-slate-800 flex items-center gap-2">
              <FileText className="w-4 h-4 text-orange-600" /> Recent Quotes
            </h3>
          </div>
          <div className="divide-y divide-slate-100">
            {recentQuotes.map(q => (
              <div key={q.id} className="px-5 py-2.5 flex items-center justify-between cursor-pointer hover:bg-blue-50/50 transition-colors group"
                onClick={() => navigate(`/quotes/${q.id}`)}>
                <div>
                  <span className="font-mono text-sky-800 font-bold text-sm">{q.quote_number || q.quote_id}</span>
                  <span className="text-slate-800 ml-2 text-sm">{q.client_name}</span>
                </div>
                <span className="text-emerald-700 font-bold tabular-nums">{fmt(q.total_amount || q.awarded_price || q.subtotal || q.estimated_cost)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// HR DASHBOARD — People focused
// ══════════════════════════════════════════════════════════════
export function HRDashboard({ user }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const config = { headers: { Authorization: `Bearer ${token}` } };
    Promise.all([
      axios.get(`${API}/dashboard/stats`, config).catch(() => ({ data: {} })),
      axios.get(`${API}/users`, config).catch(() => ({ data: [] })),
      axios.get(`${API}/hr/pending-onboarding`, config).catch(() => ({ data: { employees: [] } })),
    ]).then(([statsR, usersR, onbR]) => {
      const users = Array.isArray(usersR.data) ? usersR.data : usersR.data.users || [];
      setData({
        stats: statsR.data,
        totalEmployees: users.filter(u => u.is_active !== false).length,
        pendingOnboarding: onbR.data?.employees?.length || 0,
        expiringCerts: statsR.data?.expiring_soon || 0,
        expiredCerts: statsR.data?.expired_tickets || 0,
      });
      setLoading(false);
    });
  }, []);

  if (loading) return <DashboardSkeleton name={user?.full_name} />;

  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">HR Dashboard</h1>
        <p className="text-slate-600 text-sm mt-1">People & compliance overview</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <StatCard label="Active Employees" value={data.totalEmployees} color="blue" />
        <StatCard label="Pending Onboarding" value={data.pendingOnboarding} color="amber" />
        <StatCard label="Expiring Certs" value={data.expiringCerts} color="orange" />
        <StatCard label="Expired Certs" value={data.expiredCerts} color="red" />
      </div>

      <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-6 text-center">
        <Users className="w-10 h-10 text-blue-600 mx-auto mb-3" />
        <p className="text-slate-800 font-semibold">Use the sidebar to manage employees, onboarding, and safety certificates.</p>
      </div>
    </div>
  );
}

// ── Shared components ──
function StatCard({ label, value, color }) {
  const accent = {
    emerald: 'border-t-emerald-600 text-emerald-950', green: 'border-t-emerald-600 text-emerald-950', blue: 'border-t-blue-600 text-blue-950',
    amber: 'border-t-amber-500 text-amber-950', orange: 'border-t-orange-500 text-orange-950', red: 'border-t-red-600 text-red-950', slate: 'border-t-slate-400 text-slate-900',
  }[color] || 'border-t-slate-400 text-slate-900';
  return (
    <div className={`rounded-xl bg-white border border-slate-200/90 border-t-2 p-4 shadow-sm hover:border-blue-300 transition-colors ${accent}`} data-testid={`stat-card-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <p className="text-[11px] text-slate-500 uppercase tracking-wider font-bold">{label}</p>
      <p className="text-2xl sm:text-3xl font-black mt-1 tracking-tight tabular-nums">{value ?? 0}</p>
    </div>
  );
}

function DashboardSkeleton({ name }) {
  return (
    <div className="animate-fade-in">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">Welcome{name ? `, ${name.split(' ')[0]}` : ''}</h1>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map(i => (
          <div key={i} className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-5 animate-pulse">
            <div className="h-3 w-16 bg-slate-200 rounded mb-3" />
            <div className="h-8 w-12 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Announcements on Dashboard ──
const ANN_POSTER_ROLES = ['super_admin', 'office_admin', 'project_manager', 'project_manager_estimator', 'fabrication_manager', 'dispatch'];
const ANN_PRIORITY_STYLE = {
  normal: 'bg-slate-100 text-slate-700 border border-slate-200',
  important: 'bg-amber-100 text-amber-900 border border-amber-300',
  critical: 'bg-red-100 text-red-900 border border-red-300',
};

export function DashboardAnnouncements() {
  const { user } = useAuth();
  const canPost = ANN_POSTER_ROLES.includes(user?.role);
  const [announcements, setAnnouncements] = useState([]);
  const [showCompose, setShowCompose] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [priority, setPriority] = useState('normal');
  const [mandatory, setMandatory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [viewing, setViewing] = useState(null); // announcement opened for full read

  const load = () => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/announcements/recent`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setAnnouncements(r.data || []))
      .catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!title.trim() || !message.trim()) { toast.error('Add a title and message'); return; }
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/announcements/create`,
        { title: title.trim(), message: message.trim(), priority, mandatory },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(mandatory ? 'Posted — everyone must sign off' : 'Announcement posted');
      setTitle(''); setMessage(''); setPriority('normal'); setMandatory(false); setShowCompose(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to post');
    }
    setSaving(false);
  };

  // Nothing to show and can't post → render nothing
  if (announcements.length === 0 && !canPost) return null;

  return (
    <div data-testid="dashboard-announcements" className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm overflow-hidden border-l-4 border-l-blue-600">
      <div className="px-4 py-2.5 bg-slate-50/80 border-b border-slate-200/70 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Briefcase className="w-4 h-4 text-blue-700" />
          <h3 className="text-xs font-bold uppercase tracking-wider text-blue-900">Announcements</h3>
        </div>
        {canPost && (
          <button data-testid="dashboard-new-announcement-btn" onClick={() => setShowCompose(true)}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-semibold transition-colors">
            + New Announcement
          </button>
        )}
      </div>
      {announcements.length === 0 ? (
        <div className="px-4 py-5 text-center text-xs text-slate-400">No announcements yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {announcements.map(a => (
            <button key={a.id} type="button" data-testid={`announcement-item-${a.id}`} onClick={() => setViewing(a)}
              className="w-full text-left px-4 py-3 hover:bg-blue-50/40 transition-colors cursor-pointer">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-sm font-semibold text-slate-900">{a.title}</p>
                {a.priority && a.priority !== 'normal' && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${ANN_PRIORITY_STYLE[a.priority] || ANN_PRIORITY_STYLE.normal}`}>{a.priority}</span>
                )}
              </div>
              <p className="text-xs text-slate-600 mt-0.5 whitespace-pre-wrap line-clamp-3">{a.message}</p>
              <div className="flex items-center justify-between mt-1">
                <p className="text-[10px] text-slate-400">
                  {a.created_by ? `${a.created_by} · ` : ''}{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}
                </p>
                <span className="text-[10px] font-semibold text-blue-600">Tap to read →</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCompose && createPortal((
        <div className="fixed inset-0 z-[9000] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowCompose(false)}>
          <div data-testid="ann-compose-modal" className="bg-white rounded-xl shadow-xl w-full max-w-md p-5" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-bold text-slate-900 mb-3">New Announcement</h3>
            <input data-testid="ann-title-input" value={title} onChange={e => setTitle(e.target.value)} placeholder="Title"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none mb-2 focus:border-blue-500" />
            <textarea data-testid="ann-message-input" value={message} onChange={e => setMessage(e.target.value)} placeholder="Announcement message..." rows={4}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none resize-none mb-2 focus:border-blue-500" />
            <select data-testid="ann-priority-select" value={priority} onChange={e => setPriority(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm text-slate-900 outline-none mb-3 focus:border-blue-500">
              <option value="normal">Normal</option>
              <option value="important">Important</option>
              <option value="critical">Critical</option>
            </select>
            <label className="flex items-center gap-2 mb-4 cursor-pointer">
              <input data-testid="ann-mandatory-toggle" type="checkbox" checked={mandatory} onChange={e => setMandatory(e.target.checked)} className="w-4 h-4" />
              <span className="text-xs text-slate-700">Require sign-off (blocks the app until each person signs)</span>
            </label>
            <div className="flex justify-end gap-2">
              <button data-testid="ann-cancel-btn" onClick={() => setShowCompose(false)}
                className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button data-testid="ann-submit-btn" onClick={submit} disabled={saving}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold disabled:opacity-50">
                {saving ? 'Posting...' : 'Post Announcement'}
              </button>
            </div>
          </div>
        </div>
      ), document.body)}

      {viewing && createPortal((
        <div className="fixed inset-0 z-[9000] bg-black/50 flex items-center justify-center p-4" onClick={() => setViewing(null)} data-testid="ann-view-overlay">
          <div data-testid="ann-view-modal" className="bg-white rounded-xl shadow-xl w-full max-w-md max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-base font-bold text-slate-900">{viewing.title}</h3>
                {viewing.priority && viewing.priority !== 'normal' && (
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold capitalize ${ANN_PRIORITY_STYLE[viewing.priority] || ANN_PRIORITY_STYLE.normal}`}>{viewing.priority}</span>
                )}
              </div>
              <button data-testid="ann-view-close-btn" onClick={() => setViewing(null)}
                className="text-slate-400 hover:text-slate-700 text-2xl leading-none -mt-1">×</button>
            </div>
            <div className="px-5 py-4 overflow-y-auto">
              <p className="text-sm text-slate-700 whitespace-pre-wrap">{viewing.message}</p>
            </div>
            <div className="px-5 py-3 border-t border-slate-100 text-[11px] text-slate-400">
              {viewing.created_by ? `${viewing.created_by} · ` : ''}{viewing.created_at ? new Date(viewing.created_at).toLocaleString() : ''}
            </div>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}

// ── People Events (Birthdays, Anniversaries, Reviews) ──
export function DashboardPeopleEvents() {
  const [data, setData] = useState(null);
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/dashboard/people-events`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(() => {});
  }, []);
  if (!data) return null;

  const hasContent = data.birthdays_today?.length || data.birthdays_month?.length || data.anniversaries?.length || data.my_review;
  if (!hasContent) return null;

  return (
    <div className="space-y-3">
      {/* My Review Countdown */}
      {data.my_review && (
        <div className="bg-white rounded-xl border border-amber-300 border-l-4 border-l-amber-500 shadow-sm p-4">
          <p className="text-sm font-bold text-amber-950">
            {data.my_review.days_until === 0 ? 'Your review is TODAY!' :
             `Your review is in ${data.my_review.days_until} day${data.my_review.days_until !== 1 ? 's' : ''}`}
          </p>
          <p className="text-xs text-slate-600 mt-0.5">Scheduled: {new Date(data.my_review.date + 'T00:00:00').toLocaleDateString('en-US', {month: 'long', day: 'numeric', year: 'numeric'})}</p>
        </div>
      )}

      {/* Birthdays Today */}
      {data.birthdays_today?.length > 0 && (
        <div className="bg-pink-50 rounded-xl border border-pink-200 shadow-sm p-4">
          <p className="text-sm font-bold text-pink-800 mb-1">Happy Birthday!</p>
          {data.birthdays_today.map((b, i) => (
            <p key={i} className="text-sm text-pink-700">Happy Birthday <span className="font-bold">{b.name}</span>!</p>
          ))}
        </div>
      )}

      {/* Anniversaries This Month */}
      {data.anniversaries?.length > 0 && (
        <div className="bg-emerald-50 rounded-xl border border-emerald-200 shadow-sm p-4">
          <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider mb-2">Work Anniversaries This Month</p>
          {data.anniversaries.map((a, i) => (
            <p key={i} className="text-sm text-emerald-800">
              Congratulations <span className="font-bold">{a.name}</span> on <span className="font-bold">{a.years} year{a.years !== 1 ? 's' : ''}</span> at 365ISI!
            </p>
          ))}
        </div>
      )}

      {/* Birthdays This Month */}
      {data.birthdays_month?.length > 0 && !data.birthdays_today?.length && (
        <div className="bg-white/95 rounded-xl border border-slate-200/90 shadow-sm p-4">
          <p className="text-xs font-bold text-slate-700 uppercase tracking-wider mb-2">Birthdays This Month</p>
          <div className="flex flex-wrap gap-2">
            {data.birthdays_month.map((b, i) => (
              <span key={i} className="text-xs px-2 py-1 bg-pink-50 text-pink-800 rounded-lg border border-pink-200">
                {b.name} — {b.day}{b.day === 1 ? 'st' : b.day === 2 ? 'nd' : b.day === 3 ? 'rd' : 'th'}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// MAIN DASHBOARD — Routes to the right sub-dashboard by role
// ══════════════════════════════════════════════════════════════
// VOID CHEQUE REMINDER — for employees who skipped Direct Deposit
// ══════════════════════════════════════════════════════════════
export function VoidChequeBanner() {
  const [pending, setPending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = React.useRef(null);

  const refresh = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/onboarding/status`, { headers: { Authorization: `Bearer ${token}` } });
      const od = data?.onboarding_data || {};
      setPending(Boolean(od.direct_deposit_skipped && !od.direct_deposit_voidcheque_uploaded));
    } catch (e) { /* not an onboarded employee — ignore */ }
  };

  useEffect(() => { refresh(); }, []);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/onboarding/upload-void-cheque`, fd, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Void cheque uploaded — thank you!');
      setPending(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  if (!pending) return null;

  return (
    <div data-testid="void-cheque-banner" className="rounded-xl p-5 border border-amber-300 bg-white shadow-sm border-l-4 border-l-amber-500">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-slate-900 font-bold">Action needed: Upload your void cheque</p>
            <p className="text-sm text-slate-600">You skipped the Direct Deposit form. Please upload a photo or PDF of your void cheque / direct deposit slip so payroll can set up your account.</p>
          </div>
        </div>
        <div className="shrink-0">
          <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" data-testid="void-cheque-input"
                 onChange={(e) => handleUpload(e.target.files?.[0])} />
          <button data-testid="void-cheque-upload-btn" disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="px-5 py-2.5 rounded-lg font-bold text-xs bg-amber-500 hover:bg-amber-400 text-slate-950 disabled:opacity-60 transition-colors shadow-sm">
            {uploading ? 'Uploading…' : 'Upload Void Cheque'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
export function TruckInspectionBanner() {
  const [status, setStatus] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem('token');
    axios.get(`${API}/my/truck-inspection-status`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, []);
  if (!status?.assigned || !status?.needs_inspection) return null;
  return (
    <div data-testid="truck-inspection-banner"
         className="rounded-xl border-2 border-red-500 bg-white p-4 flex flex-col sm:flex-row sm:items-center gap-3 shadow-md border-l-8 border-l-red-600">
      <AlertTriangle className="w-7 h-7 text-red-600 shrink-0 animate-pulse" />
      <div className="flex-1">
        <p className="text-red-950 font-black text-base tracking-wide" data-testid="truck-inspection-banner-title">
          WEEKLY TRUCK INSPECTION OVERDUE
        </p>
        <p className="text-xs text-slate-700 font-medium">
          Your truck {status.truck_name} has not been inspected this week
          {status.last_inspection_date ? ` — last inspection: ${status.last_inspection_date}` : ''}.
        </p>
      </div>
      <button data-testid="truck-inspection-banner-btn"
              onClick={() => navigate('/mobile/inspections')}
              className="shrink-0 px-4 py-2 rounded-lg font-bold text-xs bg-red-600 hover:bg-red-500 text-white transition-colors shadow-sm">
        Do Inspection Now
      </button>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Fixed alerts row — safety / compliance banners that can't be hidden — then the user's boards.
export function AlertsRow() {
  return (
    <div className="space-y-4" data-testid="dashboard-alerts-row">
      <PushNudge />
      <SalesLeaderboard />
      <FiscalSwitchBanner />
      <UnpackedBoxesBanner />
      <ClockInAckCard />
      <TruckInspectionBanner />
      <VoidChequeBanner />
      <PaperworkCard />
      <TrainingScheduledCard />
    </div>
  );
}

export { getRoleGroup, MAIL_ROLES, isCribUser, MySheetSchedule, FleetAlertsCard, ToolCribTiles, DailyBriefCard, OpsCells, MailWidget, CalendarStrip, TimeOffCard };

const Dashboard = () => <BoardsPage />;

export default Dashboard;
